/**
 * Transactions API (main namespace, JWT).
 * GET /v1/transactions, GET /v1/transactions/stats, GET /v1/transactions/export
 */

import type { GasAccountBalanceData } from "@/services/gas-account"
import authorizedRequest from "@/lib/axios/authorizedRequest"

export interface Transaction {
  id: string
  tx_id: string
  tx_hash: string | null
  chain_id: number
  sponsored_amount: string
  sponsored_amount_usd: string
  status: "PENDING" | "CONFIRMED" | "CANCELLED" | "FAILED"
  created_at: string
  updated_at: string
  network: { chain_id: number }
}

export interface TransactionListMeta {
  current_page: number
  next_page: number | null
  prev_page: number | null
  total_pages: number
  total_count: number
}

export interface TransactionListResponse {
  status: boolean
  data: Transaction[]
  meta: TransactionListMeta
}

export interface SponsoredAmountPerDate {
  date: string
  sponsored_amount_usd: string
}

export interface TransactionStatsData {
  sponsored_amount_usd_per_date: SponsoredAmountPerDate[]
  gas_account_balance: GasAccountBalanceData | null
}

export interface TransactionStatsResponse {
  status: boolean
  data: TransactionStatsData
}

export interface GetTransactionsParams {
  page?: number
  per?: number
}

export async function getTransactions(
  params?: GetTransactionsParams
): Promise<TransactionListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.page != null) {
    searchParams.append("page", String(params.page))
  }
  if (params?.per != null) {
    searchParams.append("per", String(params.per))
  }
  const query = searchParams.toString()
  const url = query ? `/v1/transactions?${query}` : "/v1/transactions"
  return authorizedRequest.get(url) as Promise<TransactionListResponse>
}

export async function getTransactionStats(): Promise<TransactionStatsResponse> {
  return authorizedRequest.get(
    "/v1/transactions/stats"
  ) as Promise<TransactionStatsResponse>
}

export async function exportTransactions(): Promise<Blob> {
  const blob = (await authorizedRequest.get("/v1/transactions/export", {
    responseType: "blob",
  })) as unknown as Blob
  return blob
}
